// Full-page chrome for the New Season editors (routes registered in Main.jsx):
//   /farming-unit-editor/:distributionId/:farmingUnitId
//   /season-editor/:distributionId/:farmingUnitId
// Renders nothing for users without the `new_season` flag (redirects home).
// The page covers the app header (fixed, above it) because the design has its
// own header: logo · Back to dashboard · FARMING UNIT | SEASONS · bell/settings.
const React = require('react');
const { useEffect, useRef, useCallback } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useParams, useNavigate, Navigate, Link } = require('react-router-dom');
const { useTranslation } = require('react-i18next');
const authenticateActions = require('authenticateActions');
const helpers = require('helpers');
const {
  load_new_season_tree,
  newSeasonTreeKey,
} = require('@actions/newSeasonActions');
const NotificationsCenterHeaderContainer = require('../general/NotificationsCenter/NotificationsCenterHeaderContainer');
const { useNewSeasonFlag } = require('./useNewSeasonFlag');
const { useV2Distributions } = require('./useV2Distributions');
const { registerNewSeasonTranslations } = require('./newSeasonTranslations');
const {
  farmingUnitEditorUrl,
  seasonEditorUrl,
} = require('./newSeasonConstants');
const { LoadingState } = require('./NewSeasonUi');
const FarmingUnitEditor = require('./FarmingUnitEditor');
const SeasonEditor = require('./SeasonEditor');
require('figmaCSS');
require('./newSeason.scss');

registerNewSeasonTranslations();

const EDITORS = Object.freeze({
  FARMING_UNIT: 'farming_unit',
  SEASONS: 'seasons',
});

const logo = require('images/general/ndrip-logo.png?lastmod=12345678');
const preferencesIcon = require('images/icons/header/preferences_icon.svg');

// Leaving with unsaved edits: browser unload + every in-page navigation.
const useUnsavedChangesGuard = () => {
  const dirtyRef = useRef(false);
  const { t } = useTranslation();
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (dirtyRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
  const setDirty = useCallback((isDirty) => {
    dirtyRef.current = isDirty;
  }, []);
  const confirmLeave = useCallback(
    () => !dirtyRef.current || window.confirm(t('new_season_unsaved_changes')),
    [t],
  );
  return { setDirty, confirmLeave };
};

const PreferencesMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <div className='dropdown mr-3'>
      <div
        className='notificationsBtn ns-header-icon'
        data-toggle='dropdown'
        aria-haspopup='true'
        aria-expanded='false'
        role='button'
        aria-label={t('preferences')}
      >
        <img src={preferencesIcon} alt='' />
      </div>
      <div className='dropdown-menu dropdown-menu-right font-weight-normal text-size-16'>
        <Link to='/' className='dropdown-item text-grey45'>
          {t('home')}
        </Link>
        <Link to='/Account#' className='dropdown-item text-grey45'>
          {t('account')}
        </Link>
        <div className='dropdown-divider' />
        <a
          className='dropdown-item text-grey45'
          href='#'
          onClick={() => dispatch(authenticateActions.logout())}
        >
          {t('logout')}
        </a>
      </div>
    </div>
  );
};

const EditorTab = ({ label, active, onClick }) => (
  <button
    type='button'
    className={'ns-header-tab' + (active ? ' ns-header-tab-active' : '')}
    onClick={onClick}
  >
    {label}
  </button>
);

const EditorHeader = ({
  editor,
  onNavigate,
  distributionId,
  farmingUnitId,
}) => {
  const { t } = useTranslation();
  const email = useSelector(
    (state) => state.authentication_data?.user_details?.email,
  );
  const hasNotifications = helpers.getNotificationsCenterAccess(email);
  return (
    <div className='ns-header'>
      <div className='ns-header-left'>
        <img src={logo} className='logo-image-height' alt='N-Drip Logo' />
        <button
          type='button'
          className='ns-link-btn'
          onClick={() => onNavigate('/')}
        >
          ‹ {t('new_season_back_to_dashboard')}
        </button>
      </div>
      <div className='ns-header-tabs'>
        <EditorTab
          label={t('new_season_tab_farming_unit')}
          active={editor === EDITORS.FARMING_UNIT}
          onClick={() =>
            onNavigate(farmingUnitEditorUrl(distributionId, farmingUnitId))
          }
        />
        <EditorTab
          label={t('new_season_tab_seasons')}
          active={editor === EDITORS.SEASONS}
          onClick={() =>
            onNavigate(seasonEditorUrl(distributionId, farmingUnitId))
          }
        />
      </div>
      <div className='ns-header-right'>
        {hasNotifications && (
          <NotificationsCenterHeaderContainer openFromMainDashboard={false} />
        )}
        <PreferencesMenu />
      </div>
    </div>
  );
};

const useFarmingUnitTree = (distributionId, farmingUnitId) => {
  const dispatch = useDispatch();
  const treeKey = newSeasonTreeKey(distributionId, farmingUnitId);
  const slice = useSelector((state) => state.new_season_data);
  const reload = useCallback(
    () => dispatch(load_new_season_tree(distributionId, farmingUnitId)),
    [dispatch, distributionId, farmingUnitId],
  );
  useEffect(() => {
    reload();
  }, [reload]);
  const isCurrent = slice?.tree_key === treeKey;
  return {
    tree: isCurrent ? slice.tree : null,
    status: isCurrent ? slice.tree_status : 'init',
    error: isCurrent ? slice.tree_error : '',
    reload,
  };
};

const TreeError = ({ error, onRetry }) => {
  const { t } = useTranslation();
  return (
    <div className='ns-card ns-center-message'>
      <div className='ns-error-text'>
        {t('new_season_load_failed', { message: error })}
      </div>
      <button
        type='button'
        className='ns-btn ns-btn-outline mt-3'
        onClick={onRetry}
      >
        {t('new_season_retry')}
      </button>
    </div>
  );
};

const EditorBody = ({
  editor,
  distributionId,
  farmingUnitId,
  guard,
  onNavigate,
}) => {
  const treeState = useFarmingUnitTree(distributionId, farmingUnitId);
  if (!treeState.tree) {
    return treeState.status === 'fail' ? (
      <TreeError error={treeState.error} onRetry={treeState.reload} />
    ) : (
      <LoadingState />
    );
  }
  const props = {
    distributionId,
    farmingUnitId,
    tree: treeState.tree,
    reloadTree: treeState.reload,
    setDirty: guard.setDirty,
    onNavigate,
  };
  return editor === EDITORS.FARMING_UNIT ? (
    <FarmingUnitEditor key={distributionId + farmingUnitId} {...props} />
  ) : (
    <SeasonEditor key={distributionId + farmingUnitId} {...props} />
  );
};

const NewSeasonPage = ({ editor }) => {
  const { distributionId, farmingUnitId } = useParams();
  const navigate = useNavigate();
  // Distributions / farming units come only from GET v2/distributions.
  useV2Distributions();
  const flag = useNewSeasonFlag(distributionId, farmingUnitId);
  const guard = useUnsavedChangesGuard();

  const onNavigate = useCallback(
    (url) => {
      if (guard.confirmLeave()) {
        guard.setDirty(false);
        navigate(url);
      }
    },
    [guard, navigate],
  );

  if (flag.loaded && !flag.enabled) {
    return <Navigate to='/' replace />;
  }
  return (
    <div className='ns-page'>
      <EditorHeader
        editor={editor}
        onNavigate={onNavigate}
        distributionId={distributionId}
        farmingUnitId={farmingUnitId}
      />
      <div className='ns-page-body'>
        {flag.enabled ? (
          <EditorBody
            editor={editor}
            distributionId={distributionId}
            farmingUnitId={farmingUnitId}
            guard={guard}
            onNavigate={onNavigate}
          />
        ) : (
          <LoadingState />
        )}
      </div>
    </div>
  );
};

const FarmingUnitEditorPage = () => (
  <NewSeasonPage editor={EDITORS.FARMING_UNIT} />
);
const SeasonEditorPage = () => <NewSeasonPage editor={EDITORS.SEASONS} />;

module.exports = { FarmingUnitEditorPage, SeasonEditorPage };
